<script setup lang="ts">
    import { DButton } from '@digistorm/spark'

    const props = withDefaults(
        defineProps<{
            gateUid: string
            gateType: 'saml' | 'password'
            returnUrl?: string
            theme?: 'brand' | 'outline' | 'destructive' | 'text-link'
            size?: 'xs' | 'sm' | 'md' | 'lg'
            label?: string
        }>(),
        {
            theme: 'outline',
            size: 'sm',
            label: 'Logout',
        },
    )

    const emit = defineEmits<{
        'logging-out': [{ gateUid: string; gateType: string }]
        'logged-out': [{ gateUid: string; gateType: string }]
        error: [message: string]
    }>()

    // Composables for both gate types
    const { isLoggingOut: samlLoggingOut, logoutError: samlError, logout: samlLogout } = useSamlLogout()
    const { clearAuth, isLoggingOut: passwordLoggingOut } = useProtectedContent()

    // Unified loading/error state
    const isLoading = computed(() => {
        return props.gateType === 'saml' ? samlLoggingOut.value : passwordLoggingOut.value
    })

    const errorMessage = computed(() => {
        return props.gateType === 'saml' ? samlError.value : null
    })

    /**
     * Handle SAML logout
     */
    async function handleSamlLogout() {
        const result = await samlLogout({
            gateUid: props.gateUid,
            returnUrl: props.returnUrl || window.location.href,
        })

        if (!result.redirected) {
            emit('logged-out', { gateUid: props.gateUid, gateType: props.gateType })
        }

        if (samlError.value) {
            emit('error', samlError.value)
        }
    }

    /**
     * Handle password gate logout
     */
    async function handlePasswordLogout() {
        try {
            // Clear auth token for this gate
            await clearAuth(props.gateUid)

            emit('logged-out', { gateUid: props.gateUid, gateType: props.gateType })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Logout failed'
            emit('error', message)
            console.error('Password gate logout error:', error)
        }
    }

    /**
     * Handle logout based on gate type
     */
    async function handleLogout() {
        emit('logging-out', { gateUid: props.gateUid, gateType: props.gateType })
        if (props.gateType === 'saml') {
            await handleSamlLogout()
        } else {
            await handlePasswordLogout()
        }
    }
</script>

<template>
    <div class="logout-wrapper">
        <DButton
            :loading="isLoading"
            :disabled="isLoading"
            :theme="theme"
            :size="size"
            class="logout-button"
            @click="handleLogout"
        >
            {{ label }}
        </DButton>

        <p
            v-if="errorMessage"
            class="error-message"
        >
            {{ errorMessage }}
        </p>
    </div>
</template>

<style scoped>
    .logout-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 0.5rem;
        padding: 1rem;
    }

    .logout-button {
        min-width: auto;
        padding: 0.25rem 0.75rem;
        font-size: 0.875rem;
    }

    .error-message {
        font-size: 0.75rem;
        color: var(--color-error, #dc2626);
        margin: 0;
        text-align: right;
        background: white;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
</style>
